import React, { useEffect, useRef, useState } from "react";
import { RCCAlert, RCCButton, RCCDropDownItem, RCCGrid, RCCHookToast, RCCIcon, RCCInput, RCCServicesApi, RCCTooltip } from "react-common-components";
import { useForm } from "react-hook-form";
import FiltersContainer from "src/components/FiltersContainer";
import Select from "src/components/Select";
import SidebarCard from "src/components/SidebarCard";
import { useLoaderBackdrop } from "src/context/LoaderBackdrop/context";
import { useTranslation } from "src/context/Translations/context";
import { standardErrorMessages } from "src/utils/errors";
import PageTitle from "src/components/PageTitle";
import { IRefSidebarCard, ISidebarCardItem } from "src/components/SidebarCard/interface";
import AppIcons from "src/components/AppIcons";
import CheckPermission from "src/components/CheckPermission";
import { PermissionEnum } from "src/context/Auth/interface";
import { useAuth } from "src/context/Auth/context";
import { IMatbb001, IRefBusinessDiagnosticsModal } from "./interface";
import ModalAddChangeDelete from "./modals";
import ContentMarketShare, { IRefContentMarkShare } from "./components/contentMarketShare";


const MarketShare: React.FC = () => {

	const { translate } = useTranslation();
	const { addToast } = RCCHookToast();
	const { setLoading } = useLoaderBackdrop();
	const { administrator, superUser } = useAuth();
	
	const [cards, setCards] = useState<IMatbb001[]>([]);
	const [selectedMarketShare, setSelectedMarketShare] = useState<IMatbb001 | null>(null);
	const [showFilters, setShowFilters] = useState(false);
	const [filters, setFilters] = useState<{ filter: string ,active:string}>({ filter: "",active:"S" });
	const refModalAddChangeDelete = useRef<IRefBusinessDiagnosticsModal>(null);
	const refSidebarCard = useRef<IRefSidebarCard>(null);
	const refContentMarketShare = useRef<IRefContentMarkShare>(null);
	

	const queryListMarketShare = async (selectNewModel?: boolean) => {
		const hasRegister = cards.map(({ mab001Id }) => mab001Id);
		setLoading(true);
		try{
			const response = await RCCServicesApi.getRequest({
				method: "MarketShare/List",
				urlParameters: {
					...filters
				}
			});

			const data = response.data as IMatbb001[];
			setCards(data);

			if(selectNewModel){
				const newRegister = data.find((obj) => !hasRegister.includes(obj.mab001Id));
				if(newRegister)
				  refSidebarCard.current?.onClickCardFuncion(newRegister.mab001Id, newRegister.mab001Titulo, newRegister);
			}

			setLoading(false);
		}catch(err){
			setLoading(false);
			standardErrorMessages(err, translate("Erro ao carregar modelos de diagnóstico"), addToast);
		}
	};

	const submitFilters = (data: any) => {
		setSelectedMarketShare(null);
		refSidebarCard.current?.onClickCardFuncion(0, "", null);
		setFilters({ filter: data.filter, active:data.active.value });
	};

	const { 
		handleSubmit : handleSubmitFilters, 
		control: controlFilters, 
		reset: resetFilters,
		register: registerFilters,
		watch: watchFilters
	} = useForm();

	const callbackDeleteModel = (id: number) => {
		setCards((prevState) => prevState.filter(({ mab001Id }) => mab001Id !== id));
		if(selectedMarketShare?.mab001Id === id)
			setSelectedMarketShare(null);
	};

	useEffect(() => {
		queryListMarketShare();
	}, [filters]);

	useEffect(() => {
		if(!selectedMarketShare)
			return;
		refContentMarketShare.current?.loadData(selectedMarketShare.mab001Id);
	}, [selectedMarketShare, cards]);

	const onCallbackAddMarketShare = async () => {
		await queryListMarketShare(true);
	};

	return (
		<>
			<RCCGrid
				container
				item
				direction="column"
			>
		
				<PageTitle
					title={translate("Market Share")}
					icon={() => <AppIcons name="analysis" />}
				/>

				<SidebarCard 
				  ref={refSidebarCard}
					buttonsContent={
						<>
							<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
								<RCCTooltip
									text={translate("Nova Análise")}
									color="purple"
								>
									<RCCButton
										icon={{name: "plus", color: "#FFF", size: 16}}
										color="purple"
										layout="isRounded"
										size="md"
										onClick={() => refModalAddChangeDelete.current?.actionButtonAdd()}
									/>
								</RCCTooltip>
							</CheckPermission>
							<RCCTooltip
								text={translate("Filtros")}
								color={showFilters || filters.filter || filters.active !== "T" ? "blue" : "grey"}
							>
								<RCCButton
									icon={{name: "filter", color: "#FFF", size: 16}}
									color={showFilters || filters.filter || filters.active !== "T" ? "blue" : "grey"}
									layout="isRounded"
									size="md"
									onClick={() => setShowFilters((prevState) => !prevState)}
								/>
							</RCCTooltip>
						</>
					}
					filterContent={
						<>
							<FiltersContainer
								onSubmitFilters={handleSubmitFilters(submitFilters)}
								resetFilters={() => resetFilters({ filter: "", active: {value: "T", label: translate("Todos")}})}
								showFilters={showFilters}
							>
								<RCCGrid
									container
									item
								>
									<RCCGrid
										container 
										item
										xs={12}
									>
										<Select 
											control={controlFilters}
											name="active" 
											options={[
												{value: "S", label: translate("Sim")}, 
												{value: "N", label: translate("Não")},
												{value: "T", label: translate("Todos")}
											]} 
											label={translate("Ativos")}
											defaultValue={{value: "S", label: translate("Sim")}}
											submit="value and label"
										/>
									</RCCGrid>
									<RCCGrid
										container 
										item
										xs={12}
									>
										<RCCInput
											name="filter" label={translate("Pesquisar")}
											register={registerFilters}
											defaultValue={watchFilters("filter")}
										/>
									</RCCGrid>
								</RCCGrid>
							</FiltersContainer>
						</>
					}
					cards={cards.map((marketShare) => ({
						id: marketShare.mab001Id,
						title: marketShare.mab001Titulo,
						subtitle: marketShare.mab001Ano,
						selected: marketShare.mab001Id === selectedMarketShare?.mab001Id,
						filtered: true,
						active: marketShare.mab001Ativo === "S",
						permission: marketShare.permission
					})).filter((e) => e.permission >= 1 || administrator || superUser)}
					options={(card: ISidebarCardItem) => {
						if(!(administrator || superUser || card.permission >= 2)) return undefined;
						return (<>
							<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
								<RCCDropDownItem
									selfEnclose
									onClick={(e) => {
										e.stopPropagation();
										refModalAddChangeDelete.current?.actionButtonChange(card.id);
									} }
								>
									<RCCIcon name="pencil" />
									{translate("Alterar")}
								</RCCDropDownItem>
							</CheckPermission>
							<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE_DELETE}>
								<RCCDropDownItem 
									selfEnclose
									onClick={(e) => {
										e.stopPropagation();
										refModalAddChangeDelete.current?.actionButtonDelete(card.id, card.title);
									}}
								>
									<RCCIcon name="trash" />
									{translate("Excluir")}
								</RCCDropDownItem>	
							</CheckPermission>
						</>
						);
					}}
					cardClick={(id) => {
						const element = cards.find((item) => item.mab001Id === id);
						if(element)
							setSelectedMarketShare(element);
						else
							setSelectedMarketShare(null);
					}}
					content={
						<>
							{selectedMarketShare ? (
								<ContentMarketShare
									ref={refContentMarketShare}
								/>
							): (
								<RCCAlert 
									title={translate("Atenção")}
									description={translate("Nenhum market share selecionado")}
								/>
							)}
						</>
					}
				/>
			</RCCGrid>
			<ModalAddChangeDelete 
				ref={refModalAddChangeDelete} 
				onCallbackAdd={onCallbackAddMarketShare}
				onCallbackChange={queryListMarketShare}
				onCallbackDelete={callbackDeleteModel}
			/>
		</>
	);

};
	
export default MarketShare;
